import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { yellow } from '@carbon/colors';
import { Layer, Source } from 'react-map-gl';
import type { FillPaint, LinePaint } from 'mapbox-gl';
import { buildPolygonStyle } from 'lib/map';
import { EVAL3_SCOPED_LAYER_IDS } from 'components/map';
import { colorRulesAtom, scopedMapLayersAtom } from './atoms';
import {
  useCalcLifecycle,
  useColorElements,
  usePolygonHighlightFilter,
} from './hooks';
import { buildColorExpression, packageMapSourceUrl } from './utils';

/**
 * Tract polygon fill + outline colors (mock-ui-ux.md §Map): `yellow[30]` (#f1c21b)
 * fill, `yellow[60]` (#8e6a00) outline. Also the color-by-attribute defaults — a
 * tract matching no color rule keeps its flat fill / outline.
 */
const TRACT_FILL_COLOR = yellow[30];
const TRACT_LINE_COLOR = yellow[60];

/** Tract polygon style: yellow fill at 0.4 opacity with the outline above. */
const TRACT_STYLE = buildPolygonStyle({
  color: TRACT_FILL_COLOR,
  lineColor: TRACT_LINE_COLOR,
  fillOpacity: 0.4,
  lineOpacity: 1,
  lineWidth: 1.25,
});

interface TractLayerProps {
  /** The package whose scoped tract tiles are rendered (from `PackageMap`). */
  packageId: number;
  /** The eval-calc id (calc mode) or null — threaded into color/visibility resolution. */
  calculationId: number | null;
}

/**
 * Package-scoped Tract map layer — the `assets` MVT tile rendered as a polygon
 * fill + outline (centroid label points excluded). Visibility is driven by the
 * `tracts` key of `scopedMapLayersAtom` via Mapbox `layout.visibility`, so the
 * source stays mounted and toggling never refetches tiles. The filter is always
 * shape-only; when a row is pinned it additionally restricts to that row's related
 * tracts (by `name`).
 *
 * The fill AND outline are colored by the assets dataset's color rules: the enabled,
 * map-targeting rules resolve to ids (`useColorElements`) that paint matching tracts
 * (by `name`) their rule color via a Mapbox `match` on both `fill-color` and
 * `line-color`, each falling back to the flat fill / outline color.
 *
 * @param {TractLayerProps} props - The package id + eval-calc id the tiles are scoped to.
 * @return {JSX.Element} The tract tile source with its fill + outline layers.
 */
export function TractLayer({ packageId, calculationId }: TractLayerProps) {
  const { tracts } = useAtomValue(scopedMapLayersAtom);
  const layout = { visibility: tracts ? 'visible' : 'none' } as const;
  const { canFetch: calcTilesReady } = useCalcLifecycle(calculationId);
  // Layer filter = page-filter visibility ∩ pin (memoized in the hook — that
  // stability is load-bearing for smooth zoom; see the hook's comment).
  const filter = usePolygonHighlightFilter(packageId, calculationId);

  // Color-by-attribute (assets): resolved ids → a Mapbox `match` over the polygon
  // `name`, falling back to the flat fill. Memoized with the same discipline as the
  // filter (a fresh paint identity re-applies paint + janks zoom); `colorElements`
  // is referentially stable, so this recomputes only on a real color change.
  const colorRules = useAtomValue(colorRulesAtom('assets'));
  const mapRules = useMemo(
    () =>
      colorRules.filter((rule) => rule.enabled && rule.applyTo.includes('map')),
    [colorRules]
  );
  const colorElements = useColorElements(packageId, calculationId, mapRules);
  // Color the fill AND the outline by attribute. The match branches are identical
  // (resolved ids → rule color); only the fallback differs — an unmatched tract
  // keeps its flat fill vs its flat outline color.
  const fillColor = useMemo(
    () => buildColorExpression(colorElements, 'name', TRACT_FILL_COLOR),
    [colorElements]
  );
  const lineColor = useMemo(
    () => buildColorExpression(colorElements, 'name', TRACT_LINE_COLOR),
    [colorElements]
  );
  const fillPaint = useMemo<FillPaint>(
    () => ({ ...TRACT_STYLE.fill, 'fill-color': fillColor }),
    [fillColor]
  );
  const linePaint = useMemo<LinePaint>(
    () => ({ ...TRACT_STYLE.line, 'line-color': lineColor }),
    [lineColor]
  );

  return (
    <Source
      id="eval3-tracts"
      type="vector"
      url={packageMapSourceUrl(
        packageId,
        'assets',
        calcTilesReady ? calculationId : null
      )}
    >
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.tract.fill}
        type="fill"
        source-layer="package-assets"
        filter={filter}
        paint={fillPaint}
        layout={layout}
      />
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.tract.line}
        type="line"
        source-layer="package-assets"
        filter={filter}
        paint={linePaint}
        layout={layout}
      />
    </Source>
  );
}
